import { Container, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Events } from "app-interfaces";
import React from "react";
import { useTranslation } from "react-i18next";

import { Action } from "../../types/StaticTypes";
import { TimePickerModal } from "../Modals";
import SessionFormModal, { SessionFormView } from "../Modals/SessionFormModal";
import { SessionListings, sharedStyles } from "./SessionListings";

const throwOnUse = (view: string) => {
  throw new Error("implement me if needed. view: " + view);
};

// @ts-ignore
const useStyles = makeStyles(sharedStyles);

type ScheduleDetailsProps = {
  children?: any;
  event: Events.IAgenda; // ConcreteKey<Events.IAgenda, "session">;
  session?: Events.ISession;
  dispatch: React.Dispatch<Action>;
  [x: string]: any;
};

export default function ScheduleDetails({ event, session, dispatch }: ScheduleDetailsProps) {
  const classes = useStyles();

  switch (event.session_type.toString()) {
    case "LUNCH":
      return (
        <Container className={`${classes.container} lunch`}>
          <SessionLunch event={event} session={session} dispatch={dispatch} />
        </Container>
      );
    case "ADDNEW":
      return (
        <Container className={`${classes.container} add`}>
          <SessionAddNew event={event} session={session} dispatch={dispatch} />
        </Container>
      );
    case "BREAK":
      return (
        <Container className={`${classes.container} break`}>
          <SessionBreak event={event} session={session} dispatch={dispatch} />
        </Container>
      );
    case "OPTIONS":
    case "NORMAL":
      return (
        <Paper className={classes.paper}>
          <SessionListings event={event} session={session} dispatch={dispatch} />
        </Paper>
      );
    case "BLOCKED_TIME":
      return (
        <Container className={`${classes.container} blocked`}>
          <SessionBlocked event={event} session={session} dispatch={dispatch} />
        </Container>
      );
    default:
      return null;
  }
}

interface SessionLunchProps extends ScheduleDetailsProps {}

const SessionLunch: React.FC<SessionLunchProps> = ({ event, session, dispatch }) => {
  const { t } = useTranslation();
  const [lunch, setLunch] = React.useState<boolean>(false);
  const lunchLength =
    event.session_length / 60 >= 1
      ? `${Math.floor(event.session_length / 60)} ${t(
          Math.floor(event.session_length / 60) > 1 ? "Hrs" : "Hr"
        )} ${event.session_length % 60 > 0 ? `${event.session_length % 60} ${t("mins")}` : ""}`
      : `${event.session_length % 60} ${t("mins")}`;
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const from = formData.get("from");
    const to = formData.get("to");
    if (!event) return;
    const payload = {
      start: from + "Z",
      end: to + "Z",
      type: "LUNCH_PATCH",
      event,
    };
    dispatch({ type: "update-lunch", payload });
    return setLunch(false);
  };
  return (
    <>
      <Typography variant="h5">
        {t("LUNCH BREAK")} {lunchLength}
        <span
          style={{ display: "block" }}
          onClick={!session ? () => event.is_editable && setLunch(true) : undefined}
        >
          {t("EDIT")}
        </span>
      </Typography>
      <TimePickerModal
        show={lunch}
        title={t("LUNCH BREAK")}
        onSubmit={handleSubmit}
        onHide={() => setLunch(false)}
        currentItem={event}
      />
    </>
  );
};

interface SessionAddNewProps extends ScheduleDetailsProps {}

function extractFormData<
  X extends {
    type: "SESSION" | "BLOCKED_TIME";
    customer_id?: string[];
    from?: string;
    to?: string;
    timezone: string;
  }
>(formData: FormData, event: Events.IAgenda): X {
  const payload = {} as X;
  formData.forEach((v, k) => {
    if (k === "customer_id" && typeof v === "string")
      return Object.assign(payload, { [k]: v.split(",") });
    if ((k === "from" || k === "to") && typeof v === "string")
      return Object.assign(payload, {
        [k]: `${event.ev_start_time.split(" ")[0]}T${v}Z`,
      });
    return Object.assign(payload, { [k]: v });
  });
  Object.assign(payload, { timezone: event.timezone });

  return payload;
}

const SessionAddNew: React.FC<SessionAddNewProps> = ({ event, session, dispatch }) => {
  const { t } = useTranslation();
  const [addNew, setAddNew] = React.useState<boolean>(false);
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const payload = extractFormData(formData, event);

    dispatch({ type: "addnew", payload: { ...payload, setAddNew } });
  };
  return (
    <>
      <Typography
        variant="h5"
        onClick={
          session
            ? () => dispatch({ type: "reschedule", payload: event })
            : () => event.is_editable && setAddNew(true)
        }
      >
        {event.session_length / 60 >= 1
          ? `${Math.floor(event.session_length / 60)} ${t(
              Math.floor(event.session_length / 60) > 1 ? "Hrs" : "Hr"
            )}`
          : ""}{" "}
        {event.session_length % 60 > 0
          ? `${event.session_length % 60} ${t("mins")} ${t("AVAILABLE")}`
          : t("AVAILABLE")}
        <span>{t("ADD NEW")}</span>
      </Typography>
      {addNew && (
        <SessionFormModal
          view={SessionFormView.DEFAULT}
          setView={throwOnUse}
          show={addNew}
          onHide={() => setAddNew(false)}
          onSubmit={handleSubmit}
          event={event}
          time={{
            start: event.start.replace("Z", ""),
            end: event.end.replace("Z", ""),
            datetime: new Date(event.ev_start_time.replace(" ", "T") + "Z"),
          }}
          dispatch={dispatch}
        />
      )}
    </>
  );
};

interface SessionBreakProps extends ScheduleDetailsProps {}

const SessionBreak: React.FC<SessionBreakProps> = ({ event }) => {
  const { t } = useTranslation();
  return (
    <Typography variant="h5">
      {event.session_length} {t("mins")} {t("BREAK")}
    </Typography>
  );
};

interface SessionBlockedProps extends ScheduleDetailsProps {}

const SessionBlocked: React.FC<SessionBlockedProps> = ({ event, session, dispatch }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = React.useState(false);
  const blockedLength =
    event.session_length / 60 >= 1
      ? `${Math.floor(event.session_length / 60)} ${t(
          Math.floor(event.session_length / 60) > 1 ? "Hrs" : "Hr"
        )}  ${event.session_length % 60 > 0 ? `${event.session_length % 60} ${t("mins")}` : ""}`
      : `${event.session_length % 60} ${t("mins")}`;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const formData = new FormData(e.currentTarget);
    const payload = extractFormData(formData, event);

    if (payload.type === "SESSION" && !payload.customer_id) {
      dispatch({ type: "remove-event", payload });
    } else if (payload.type === "SESSION" && payload.customer_id) {
      dispatch({ type: "addnew", payload });
    } else if (payload.type === "BLOCKED_TIME") {
      dispatch({ type: "update-blocked", payload });
    }
    return setShowModal(false);
  };

  return (
    <>
      <Typography variant="h5">
        {blockedLength} {t("PERSONAL TIME")}
        <span
          style={{ display: "block" }}
          onClick={!session ? () => event.is_editable && setShowModal(true) : undefined}
        >
          {t("EDIT")}
        </span>
      </Typography>
      {/* <span>{event.name && event.name}</span> */}
      <SessionFormModal
        view={SessionFormView.DEFAULT}
        setView={throwOnUse}
        show={showModal}
        onHide={() => setShowModal(false)}
        onSubmit={handleSubmit}
        event={event}
        time={{
          start: event.start.replace("Z", ""),
          end: event.end.replace("Z", ""),
          datetime: new Date(event.ev_start_time.replace(" ", "T") + "Z"),
        }}
        dispatch={dispatch}
      />
    </>
  );
};
